import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Key, Shield, Lock } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов генератора
import PasswordGeneratorForm from './PasswordGeneratorPage/components/PasswordGeneratorForm';
import PasswordStrengthMeter from './PasswordGeneratorPage/components/PasswordStrengthMeter';
import PasswordHistory from './PasswordGeneratorPage/components/PasswordHistory';
import SecurityTipsSidebar from './PasswordGeneratorPage/components/SecurityTipsSidebar';
import PasswordRequirements from './PasswordGeneratorPage/components/PasswordRequirements';
import InfoContent from './PasswordGeneratorPage/components/InfoContent';
import SavedPasswordsSidebar from './PasswordGeneratorPage/components/SavedPasswordsSidebar';
import PasswordGeneratorSuggestions from './PasswordGeneratorPage/components/PasswordGeneratorSuggestions';

// Определение начального состояния формы
export const initialFormData = {
  length: 12,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSymbols: false,
  excludeSimilarChars: false,
  excludeAmbiguous: false,
  passwordType: 'random', // 'random', 'memorable', 'pin', 'phrase'
  wordCount: 4, // для memorable
  wordSeparator: '-', // для memorable
  pinLength: 4, // для pin
  phraseLength: 4, // для phrase
  includeRandomCase: false, // для memorable и phrase
  includeRandomNumbers: false, // для memorable и phrase
};

// Определение типов паролей
export const passwordTypes = [
  { id: 'random', name: 'Случайный пароль', description: 'Криптостойкий случайный набор символов' },
  { id: 'memorable', name: 'Запоминаемый пароль', description: 'Набор случайных слов, которые легче запомнить' },
  { id: 'pin', name: 'PIN-код', description: 'Числовой пароль для банковских карт и устройств' },
  { id: 'phrase', name: 'Парольная фраза', description: 'Длинная фраза с высокой энтропией' }
];

// Слова для генерации запоминаемых паролей
export const russianWords = [
  // Существительные
  'дом', 'стол', 'окно', 'книга', 'день', 'вода', 'лес', 'город', 'море', 'река',
  'гора', 'страна', 'небо', 'земля', 'солнце', 'луна', 'звезда', 'дерево', 'цветок', 'трава',
  'кот', 'собака', 'птица', 'рыба', 'время', 'путь', 'дело', 'жизнь', 'рука', 'нога',
  'голова', 'глаз', 'нос', 'рот', 'ухо', 'сердце', 'мозг', 'кровь', 'кость', 'мышца',
  'еда', 'хлеб', 'мясо', 'молоко', 'сыр', 'яйцо', 'соль', 'сахар', 'чай', 'кофе',
  'фрукт', 'овощ', 'машина', 'поезд', 'самолет', 'корабль', 'дорога', 'мост', 'здание', 'школа',
  // Прилагательные
  'новый', 'старый', 'большой', 'малый', 'высокий', 'низкий', 'длинный', 'короткий', 'широкий', 'узкий',
  'толстый', 'тонкий', 'тяжелый', 'легкий', 'горячий', 'холодный', 'теплый', 'мокрый', 'сухой', 'чистый',
  'грязный', 'твердый', 'мягкий', 'громкий', 'тихий', 'светлый', 'темный', 'цветной', 'черный', 'белый',
  'красный', 'синий', 'зеленый', 'желтый', 'оранжевый', 'фиолетовый', 'розовый', 'серый', 'коричневый', 'золотой',
  // Глаголы
  'быть', 'иметь', 'делать', 'сказать', 'идти', 'прийти', 'видеть', 'слышать', 'знать', 'думать',
  'хотеть', 'мочь', 'любить', 'жить', 'работать', 'играть', 'есть', 'пить', 'спать', 'бежать',
  'стоять', 'сидеть', 'лежать', 'давать', 'брать', 'открыть', 'закрыть', 'начать', 'кончить', 'читать',
  'писать', 'говорить', 'учить', 'помнить', 'забыть', 'найти', 'терять', 'покупать', 'продавать', 'платить'
];

// Словарь фраз
export const russianPhrases = [
  'синяя птица счастья',
  'золотой ключик от дверей',
  'серебряное копытце в лесу',
  'красная шапочка и волк',
  'зеленый листок на ветру',
  'черная кошка перешла дорогу',
  'белый снег на ветках',
  'яркое солнце над горами',
  'тихий шепот на рассвете',
  'громкий гром после молнии',
  'быстрая река в ущелье',
  'медленный танец на закате',
  'высокие облака в небе',
  'низкий полет ласточки',
  'острый нож на столе',
  'тупой карандаш в пенале',
  'глубокое озеро в горах',
  'мелкий ручей в лесу',
  'теплый хлеб из печи',
  'холодный ветер с моря'
];

// Список требований разных сервисов к паролям
export const serviceRequirements = [
  {
    name: 'Госуслуги',
    icon: 'FileText',
    requirements: [
      'Минимум 8 символов',
      'Буквы латинского алфавита',
      'Заглавные и строчные буквы',
      'Цифры',
      'Спецсимволы не обязательны'
    ]
  },
  {
    name: 'Сбербанк Онлайн',
    icon: 'CreditCard',
    requirements: [
      'Минимум 8 символов',
      'Латинские или русские буквы',
      'Минимум одна заглавная буква',
      'Минимум одна цифра',
      'Не должен совпадать с логином'
    ]
  },
  {
    name: 'ВТБ Онлайн',
    icon: 'CreditCard',
    requirements: [
      'Минимум 8 символов',
      'Максимум 20 символов',
      'Латинские буквы',
      'Минимум одна заглавная и одна строчная буква',
      'Минимум одна цифра'
    ]
  },
  {
    name: 'Яндекс',
    icon: 'Search',
    requirements: [
      'Минимум 8 символов',
      'Можно использовать буквы, цифры и символы',
      'Не должен совпадать с логином или его частью'
    ]
  },
  {
    name: 'Mail.ru',
    icon: 'Mail',
    requirements: [
      'Минимум 8 символов',
      'Латинские буквы',
      'Минимум одна заглавная буква',
      'Минимум одна цифра'
    ]
  },
  {
    name: 'ВКонтакте',
    icon: 'MessageCircle',
    requirements: [
      'Минимум 6 символов',
      'Желательно использовать буквы и цифры',
      'Без требований к регистру'
    ]
  }
];

/**
 * PasswordGeneratorPage Component - Полнофункциональный генератор паролей с различными типами и настройками.
 */
const PasswordGeneratorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('password-generator'), []);
  const pageTitle = tool?.metaTitle || 'Генератор паролей | Gensio';
  const metaDescription = tool?.metaDescription || 'Создавайте надежные и безопасные пароли с настраиваемыми параметрами и проверкой на криптостойкость для защиты ваших онлайн-аккаунтов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [passwordHistory, setPasswordHistory] = useState([]);
  const [savedPasswords, setSavedPasswords] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('generator'); // 'generator' или 'checker'

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка истории сохраненных паролей из localStorage ---
  useEffect(() => {
    const savedPasswordsData = localStorage.getItem('savedPasswords');
    if (savedPasswordsData) {
      try {
        setSavedPasswords(JSON.parse(savedPasswordsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных паролей:", e);
      }
    }

    const passwordHistoryData = localStorage.getItem('passwordHistory');
    if (passwordHistoryData) {
      try {
        setPasswordHistory(JSON.parse(passwordHistoryData));
      } catch (e) {
        console.error("Ошибка загрузки истории паролей:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'strong-password':
          newState.length = 16;
          newState.includeUppercase = true;
          newState.includeLowercase = true;
          newState.includeNumbers = true;
          newState.includeSymbols = true;
          newState.passwordType = 'random';
          break;
        case 'readable-password':
          newState.passwordType = 'memorable';
          newState.wordCount = 4;
          newState.includeRandomCase = true;
          newState.includeRandomNumbers = true;
          break;
        case 'password-strength':
          setActiveTab('checker');
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setGeneratedPassword('');
    setPasswordStrength(null);

  }, [subToolId]);

  // Сохранение пароля в localStorage
  const savePassword = (name) => {
    if (!generatedPassword) return;

    const newSavedPassword = {
      id: Date.now().toString(),
      name: name || `Пароль ${new Date().toLocaleDateString()}`,
      password: generatedPassword,
      type: formData.passwordType,
      strength: passwordStrength.score,
      date: new Date().toISOString()
    };

    const updatedSavedPasswords = [...savedPasswords, newSavedPassword];
    setSavedPasswords(updatedSavedPasswords);
    localStorage.setItem('savedPasswords', JSON.stringify(updatedSavedPasswords));

    return newSavedPassword;
  };

  // Удаление сохраненного пароля
  const deleteSavedPassword = (id) => {
    const updatedSavedPasswords = savedPasswords.filter(pass => pass.id !== id);
    setSavedPasswords(updatedSavedPasswords);
    localStorage.setItem('savedPasswords', JSON.stringify(updatedSavedPasswords));
  };

  // Добавление пароля в историю
  const addPasswordToHistory = (password, type) => {
    if (!password) return;
    
    const newPasswordEntry = {
      id: Date.now().toString(),
      password,
      type,
      date: new Date().toISOString()
    };

    const updatedHistory = [newPasswordEntry, ...passwordHistory].slice(0, 10); // Сохраняем только последние 10 паролей
    setPasswordHistory(updatedHistory);
    localStorage.setItem('passwordHistory', JSON.stringify(updatedHistory));
  };

  // Очистка истории паролей
  const clearPasswordHistory = () => {
    setPasswordHistory([]);
    localStorage.removeItem('passwordHistory');
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Key className="w-8 h-8 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                {tool?.name || 'Генератор паролей'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* Tabs for Generator and Checker */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('generator')}
                    className={`inline-flex items-center p-4 border-b-2 rounded-t-lg ${
                      activeTab === 'generator'
                        ? 'text-violet-600 border-violet-600 dark:text-violet-400 dark:border-violet-400'
                        : 'border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                    }`}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Генератор паролей
                  </button>
                </li>
                <li className="mr-2">
                  <button
                    onClick={() => setActiveTab('checker')}
                    className={`inline-flex items-center p-4 border-b-2 rounded-t-lg ${
                      activeTab === 'checker'
                        ? 'text-violet-600 border-violet-600 dark:text-violet-400 dark:border-violet-400'
                        : 'border-transparent hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'
                    }`}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Проверка надежности
                  </button>
                </li>
              </ul>
            </div>

            {activeTab === 'generator' ? (
              /* --- Форма генератора паролей --- */
              <PasswordGeneratorForm 
                formData={formData}
                setFormData={setFormData}
                generatedPassword={generatedPassword}
                setGeneratedPassword={setGeneratedPassword}
                passwordStrength={passwordStrength}
                setPasswordStrength={setPasswordStrength}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                copied={copied}
                setCopied={setCopied}
                passwordTypes={passwordTypes}
                russianWords={russianWords}
                russianPhrases={russianPhrases}
                addPasswordToHistory={addPasswordToHistory}
                savePassword={savePassword}
              />
            ) : (
              /* --- Проверка надежности пароля --- */
              <PasswordStrengthMeter 
                passwordStrength={passwordStrength}
                setPasswordStrength={setPasswordStrength}
              />
            )}

            {/* Отображение истории паролей */}
            <PasswordHistory 
              passwordHistory={passwordHistory}
              clearPasswordHistory={clearPasswordHistory}
            />

            {/* --- Требования к паролям на популярных сервисах --- */}
            <PasswordRequirements serviceRequirements={serviceRequirements} />

            {/* --- Предложения по генерации паролей --- */}
            <PasswordGeneratorSuggestions 
              setFormData={setFormData}
              initialFormData={initialFormData}
            />

            {/* --- Информационный контент о паролях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель советов по безопасности --- */}
            <SecurityTipsSidebar />

            {/* --- Панель сохраненных паролей --- */}
            <SavedPasswordsSidebar 
              savedPasswords={savedPasswords}
              deleteSavedPassword={deleteSavedPassword}
              setShowPassword={setShowPassword}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-violet-500 focus:ring-violet-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-violet-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-violet-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        
        /* Password Strength Meter */
        .strength-meter {
          position: relative;
          height: 8px;
          background-color: #e2e8f0;
          border-radius: 4px;
          margin: 10px 0;
          overflow: hidden;
        }
        .dark .strength-meter {
          background-color: #374151;
        }
        .strength-meter-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease, background-color 0.3s ease;
        }
        .strength-0 { width: 5%; background-color: #f87171; }
        .strength-1 { width: 25%; background-color: #f87171; }
        .strength-2 { width: 50%; background-color: #facc15; }
        .strength-3 { width: 75%; background-color: #22c55e; }
        .strength-4 { width: 100%; background-color: #10b981; }
        
        /* Password Copy Button Animation */
        .copy-animation {
          animation: pulse 0.5s ease-in-out;
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PasswordGeneratorPage;